import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { GripVertical, Lock, LogOut } from 'lucide-react';

/**
 * Contorul de inactivitate + deconectarea manuală, pe un terminal public.
 *
 * Un singur element, nu două pastile separate: pe ecran arăta ca două lucruri
 * fără legătură între ele.
 *
 * Se poate muta oriunde, de mânerul din stânga, iar poziția se ține minte. Pe un
 * terminal chestia asta stă permanent peste interfață, deci va acoperi din când
 * în când exact butonul de care are nevoie omul — și nu are cum să o dea la o
 * parte altfel.
 *
 * Butonul manual contează la fel de mult ca cronometrul: 30 de secunde e
 * suficient ca cineva să se așeze în locul tău, deci cine termină apasă și pleacă.
 */

const POSITION_KEY = 'kiosk_chip_position';
const MARGIN = 16;

function loadPosition() {
  try {
    const raw = localStorage.getItem(POSITION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return typeof parsed?.x === 'number' && typeof parsed?.y === 'number' ? parsed : null;
  } catch {
    return null;
  }
}

export default function KioskIdleChip({ secondsLeft, onLogout }) {
  const seconds = secondsLeft ?? 0;
  const urgent = seconds <= 10;

  const elementRef = useRef(null);
  const dragRef = useRef(null);
  const [position, setPosition] = useState(loadPosition);

  // Poziția implicită (dreapta-jos) se calculează abia când știm dimensiunea
  // reală a elementului. La redimensionarea ferestrei îl aduc înapoi în ecran,
  // altfel un chip lăsat în dreapta pe un monitor mare dispare pe unul mic.
  useEffect(() => {
    const clampIntoView = () => {
      const el = elementRef.current;
      if (!el) return;
      const { offsetWidth: w, offsetHeight: h } = el;
      setPosition((current) => {
        const target = current ?? {
          x: window.innerWidth - w - MARGIN,
          y: window.innerHeight - h - MARGIN,
        };
        return {
          x: Math.min(Math.max(MARGIN, target.x), Math.max(MARGIN, window.innerWidth - w - MARGIN)),
          y: Math.min(Math.max(MARGIN, target.y), Math.max(MARGIN, window.innerHeight - h - MARGIN)),
        };
      });
    };

    clampIntoView();
    window.addEventListener('resize', clampIntoView);
    return () => window.removeEventListener('resize', clampIntoView);
  }, []);

  function onPointerDown(event) {
    const el = elementRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    dragRef.current = { dx: event.clientX - rect.left, dy: event.clientY - rect.top };
    // setPointerCapture: mutarea continuă chiar dacă degetul/cursorul iese de pe
    // mâner, ceea ce se întâmplă mereu la o mișcare rapidă.
    event.currentTarget.setPointerCapture(event.pointerId);
  }

  function onPointerMove(event) {
    const drag = dragRef.current;
    const el = elementRef.current;
    if (!drag || !el) return;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    setPosition({
      x: Math.min(Math.max(MARGIN, event.clientX - drag.dx), window.innerWidth - w - MARGIN),
      y: Math.min(Math.max(MARGIN, event.clientY - drag.dy), window.innerHeight - h - MARGIN),
    });
  }

  function onPointerUp(event) {
    if (!dragRef.current) return;
    dragRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    try {
      const el = elementRef.current;
      if (el) {
        const rect = el.getBoundingClientRect();
        localStorage.setItem(POSITION_KEY, JSON.stringify({ x: rect.left, y: rect.top }));
      }
    } catch {
      /* storage indisponibil — poziția ține doar cât sesiunea */
    }
  }

  return (
    <div
      ref={elementRef}
      className={[
        'fixed flex select-none items-stretch overflow-hidden rounded-2xl shadow-2xl transition-colors',
        // brand-error-dark, nu brand-error: 5,69:1 cu alb in loc de 3,96:1, si
        // asta e singurul element pe care cineva trebuie sa-l citeasca in graba.
        urgent ? 'bg-brand-error-dark' : 'bg-brand-secondary',
      ].join(' ')}
      style={{
        left: position?.x ?? -9999,
        top: position?.y ?? -9999,
        // Sub overlay-ul de blocare, peste dialogurile si toast-urile aplicatiei.
        zIndex: 2147482000,
        visibility: position ? 'visible' : 'hidden',
      }}
    >
      <div
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        className="flex cursor-grab items-center px-2 text-white/50 transition-colors hover:bg-white/10 hover:text-white/80 active:cursor-grabbing"
        title="Trage ca să mutezi"
        aria-label="Mută"
        role="button"
        tabIndex={-1}
      >
        <GripVertical className="h-6 w-6" aria-hidden="true" />
      </div>

      <div className="flex items-center gap-2 py-3 pr-4 text-white">
        <Lock className={urgent ? 'h-6 w-6' : 'h-5 w-5'} aria-hidden="true" />
        <span className={urgent ? 'font-mono text-2xl font-bold' : 'font-mono text-lg'}>
          00:{String(Math.max(0, seconds)).padStart(2, '0')}
        </span>
      </div>

      <button
        type="button"
        onClick={onLogout}
        className="flex items-center gap-2 bg-white/15 px-6 text-base font-semibold text-white transition-colors hover:bg-white/25"
      >
        <LogOut className="h-5 w-5" aria-hidden="true" />
        Deconectează
      </button>
    </div>
  );
}

KioskIdleChip.propTypes = {
  secondsLeft: PropTypes.number,
  onLogout: PropTypes.func.isRequired,
};
